import * as Sentry from "@sentry/react";

/**
 * Open the Sentry user-feedback dialog with context pre-filled (URL, theme,
 * user agent, viewport). Called by the in-app "Report a Bug" button in the
 * BrainwaveGenerator header.
 *
 * Returns true if the dialog was opened (Sentry is configured in this env),
 * false otherwise — caller should fall back to a mailto link.
 */
export async function openFeedbackDialog() {
  try {
    const client = Sentry.getClient?.();
    if (!client) return false;
    const feedback = client.getIntegrationByName?.("Feedback");
    if (!feedback) return false;

    // Pre-fill the form's message with the current URL + lightweight session
    // context so users don't have to type it. Sentry SDK v10's feedback API
    // exposes `createForm({ ...overrides })`.
    const contextLines = [
      "",
      "",
      "—— auto-captured context (please keep) ——",
      `URL: ${window.location.href}`,
      `Theme: ${localStorage.getItem("neurowave_theme") || "signature"}`,
      `Viewport: ${window.innerWidth}×${window.innerHeight}`,
      `Agent: ${navigator.userAgent}`,
    ].join("\n");

    // Also attach the URL as a Sentry tag so it shows up in the Issues list
    // without having to open the feedback body.
    Sentry.setTag("feedback_url", window.location.pathname);

    if (typeof feedback.createForm === "function") {
      const form = await feedback.createForm({
        formTitle: "Report a problem",
        messagePlaceholder: "What happened? Steps to reproduce help us fix it faster.",
      });
      if (form?.appendToDom) form.appendToDom();
      if (form?.open) form.open();

      // Try to seed the textarea with auto-captured context after the dialog mounts
      requestAnimationFrame(() => {
        const host = document.querySelector("#sentry-feedback");
        const shadow = host?.shadowRoot;
        const textarea = shadow?.querySelector("textarea");
        if (textarea && !textarea.value) {
          textarea.value = contextLines;
          textarea.dispatchEvent(new Event("input", { bubbles: true }));
        }
      });
      return true;
    }

    // Older SDKs use openDialog() directly off the integration
    if (typeof feedback.openDialog === "function") {
      feedback.openDialog();
      return true;
    }

    return false;
  } catch (e) {
    // Sentry not initialized, shadow-DOM API change, etc. — fail quietly so
    // the caller can fall back to mailto without spamming the console.
    return false;
  }
}
